import React, { useState, useRef, useMemo, useEffect } from "react";
import { Users, Search, UserCheck } from "lucide-react";
import { useGetActiveUsersStatusQuery } from "../../store/apis/user.api";
import { CARD_CLASS } from "../../constants/dashboard";
import { getAvatarGradient } from "../../utils/avatarUtils";
import Badge from "../common/Badge";

const EmployeeCard: React.FC = () => {
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const searchInputRef = useRef<HTMLInputElement>(null);

  const { data, isLoading: loading, isError: hasError, error } = useGetActiveUsersStatusQuery();

  const employees = useMemo(() => data?.users ?? [], [data]);

  const filteredEmployees = employees.filter((employee) => {
    if (!searchQuery.trim()) return true;
    const query = searchQuery.toLowerCase();
    const name = (employee.name || "").toLowerCase();
    const employeeId = (employee.employeeId || "").toLowerCase();
    const userId = (employee.userId || "").toLowerCase();
    const email = (employee.email || "").toLowerCase();
    return (
      name.includes(query) ||
      employeeId.includes(query) ||
      userId.includes(query) ||
      email.includes(query)
    );
  });

  const sortedFilteredEmployees = [...filteredEmployees].sort((a, b) =>
    (a.name || "").localeCompare(b.name || "")
  );

  const handleSearchToggle = () => {
    if (isSearchOpen) {
      setIsSearchOpen(false);
      setSearchQuery("");
    } else {
      setIsSearchOpen(true);
    }
  };

  const handleSearchBlur = () => {
    if (!searchQuery.trim()) {
      setIsSearchOpen(false);
    }
  };

  useEffect(() => {
    if (isSearchOpen) {
      searchInputRef.current?.focus();
    }
  }, [isSearchOpen]);

  const errorMessage =
    hasError && error && typeof error === "object" && "data" in error
      ? (error.data as { message?: string })?.message
      : "Failed to load employee data";

  if (hasError) {
    return (
      <div className={`${CARD_CLASS} p-4 sm:p-5 h-full min-h-[270px] flex flex-col`}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="m-0 text-heading-sm font-bold text-text-primary flex items-center gap-2 tracking-tight">
            <UserCheck className="w-[18px] h-[18px] text-primary-600" />
            Team Status
          </h3>
        </div>
        <div className="flex-1 flex flex-col items-center justify-center text-center p-4">
          <div className="w-12 h-12 bg-error-light rounded-full flex items-center justify-center mb-3">
            <Users className="w-6 h-6 text-error-dark" />
          </div>
          <p className="text-body-sm font-semibold text-text-secondary">{errorMessage}</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`${CARD_CLASS} p-4 sm:p-5 h-full min-h-[270px] flex flex-col`}>
      {/* Header */}
      <div className="flex items-center justify-between mb-3.5 shrink-0 gap-2">
        {!isSearchOpen ? (
          <h3 className="m-0 text-heading-sm font-bold text-text-primary flex items-center gap-2 tracking-tight truncate">
            <UserCheck className="w-[18px] h-[18px] text-primary-600" />
            <span>Team Status</span>
          </h3>
        ) : (
          <div className="flex-1 flex items-center gap-2 border-b border-border-secondary pb-1 transition-all duration-200">
            <input
              type="text"
              ref={searchInputRef}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onBlur={handleSearchBlur}
              placeholder="Search team..."
              className="w-full bg-transparent text-body-sm text-text-primary placeholder:text-text-tertiary focus:outline-none"
            />
          </div>
        )}

        <button
          type="button"
          onMouseDown={(e) => {
            e.preventDefault();
            handleSearchToggle();
          }}
          className="w-7 h-7 rounded-lg grid place-items-center hover:bg-layout-hover text-text-secondary transition-colors focus-visible:ring-2 focus-visible:ring-primary focus-visible:outline-none flex-none"
          aria-label="Toggle team search"
        >
          <Search className="w-4 h-4" />
        </button>
      </div>

      {/* Employees List */}
      <div className="flex-1 overflow-y-auto scrollbar-thin scrollbar-thumb-border-input pr-1">
        {sortedFilteredEmployees.length === 0 ? (
          <div className="h-full flex flex-col items-center justify-center text-center p-4">
            <div className="w-12 h-12 bg-layout-hover rounded-full grid place-items-center mx-auto mb-2 text-text-tertiary">
              <Users className="w-5 h-5" />
            </div>
            <h4 className="text-body-sm font-semibold text-text-primary mb-0.5">
              {employees.length === 0 ? "No Employees Found" : "No Matching Employees"}
            </h4>
            <p className="text-caption text-text-tertiary">
              {employees.length === 0 ? "No status updates reported." : "Try a different search."}
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {sortedFilteredEmployees.map((employee) => {
              const isOnBreak = employee.status === "on_break";
              const isOnline = employee.status === "checked_in";
              const initial = (employee.name || "U").charAt(0).toUpperCase();
              const statusLabel = isOnBreak ? "On Break" : isOnline ? "Online" : "Offline";

              return (
                <div
                  key={employee.userId}
                  className="flex items-center gap-3 p-2.5 rounded-xl border border-border-secondary bg-surface hover:bg-layout-hover transition-all duration-150 group"
                >
                  <div
                    className="w-10 h-10 rounded-xl grid place-items-center text-white font-bold text-xs flex-none shadow-xs"
                    style={{
                      background: getAvatarGradient(employee.name || "U"),
                    }}
                  >
                    {initial}
                  </div>

                  <div className="flex-1 min-w-0">
                    <p className="text-body-sm font-bold text-text-primary truncate group-hover:text-primary-700 transition-colors">
                      {employee.name || "Unknown"}
                    </p>
                    <p className="text-caption text-text-tertiary mt-0.5 truncate">
                      {employee.employeeId ? `ID: ${employee.employeeId}` : "Employee"}
                      {employee.department ? ` • ${employee.department}` : ""}
                    </p>
                  </div>

                  {/* Status Badge & Check-in Time on Right Side */}
                  <div className="flex flex-col items-end flex-none gap-0.5">
                    <Badge
                      variant={isOnBreak ? "orange" : isOnline ? "emerald" : "gray"}
                      size="small"
                      className="font-bold flex-none"
                      icon={
                        <span
                          className={`w-1.5 h-1.5 rounded-full ${
                            isOnBreak
                              ? "bg-amber-500 animate-pulse"
                              : isOnline
                              ? "bg-emerald-500 animate-pulse"
                              : "bg-slate-400"
                          }`}
                        />
                      }
                    >
                      {statusLabel}
                    </Badge>
                    {employee.checkInTime && (
                      <span className="text-[11px] font-medium text-text-tertiary whitespace-nowrap">
                        In: {new Date(employee.checkInTime).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true })}
                      </span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default EmployeeCard;